import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';
import { getRecaptchaHeaders } from '@/features/application/utils/recaptcha-token.utils';
import { useApplicationStore } from '../store/application.store';
import { useSignerStore } from '../store/signer.store';
import { useNavigationStore } from '../store/navigation.store';

interface AutosaveResponse {
  success: boolean;
  crmId?: string | null;
  created?: boolean;
  updated?: boolean;
}

export const formAutosaveService = {
  async autosave(mappingShortId?: string): Promise<AutosaveResponse> {
    try {
      if (!mappingShortId) {
        logger.warn('Autosave', 'Missing mappingShortId, skipping autosave');
        return { success: false };
      }

      const appState = useApplicationStore.getState();
      const signerState = useSignerStore.getState();
      const { structure, userShortId, crmId, recordOwnerId } = appState;
      const signerIndex = signerState.currentSigner;

      if (!structure || !userShortId) {
        logger.warn('Autosave', 'Missing structure or userShortId, skipping autosave');
        return { success: false };
      }

      // Build URL and query params
      const search = new URLSearchParams();
      if (crmId && typeof signerIndex === 'number') {
        search.set('crmId', String(crmId));
        search.set('signerIndex', String(signerIndex));
      }
      // Include recordOwnerId for Lead record creation (only used when creating new records)
      if (recordOwnerId) {
        search.set('recordOwnerId', String(recordOwnerId));
      }
      const url = `/api/record-actions/autosave/${userShortId}/${mappingShortId}${
        search.toString() ? `?${search.toString()}` : ''
      }`;

      const correlationId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random()
            .toString(16)
            .slice(2, 10)}`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-correlation-id': correlationId,
        ...(await getRecaptchaHeaders('autosave_application')),
      };

      // Attach progress headers if we can compute them
      try {
        const { getVisiblePages, getCurrentPage } = useNavigationStore.getState();
        const visible = getVisiblePages();
        const current = getCurrentPage();
        if (visible && visible.length > 0 && current) {
          const activeIndex = visible.findIndex((p) => p.id === current.id);
          if (activeIndex >= 0) {
            headers['x-page-completed-index'] = String(activeIndex);
            headers['x-page-completed-id'] = current.id;
            headers['x-total-visible-pages'] = String(visible.length);
          }
        }
      } catch (e) {
        logger.warn('Autosave', 'Failed to compute progress headers (non-blocking)', { e });
      }

      const { data } = await api.post<AutosaveResponse>(
        url,
        { structure },
        {
          headers,
        }
      );

      return data;
    } catch (error) {
      // Extract only relevant error info to avoid logging entire request payload
      const errorInfo = error instanceof Error
        ? {
            message: error.message,
            name: error.name,
            // Include Axios response error data if available (but not the request config)
            status: (error as { response?: { status?: number } }).response?.status,
            responseData: (error as { response?: { data?: unknown } }).response?.data,
          }
        : { message: String(error) };
      logger.warn('Autosave', 'Autosave failed', errorInfo);
      return { success: false };
    }
  },
};

